import { Box, Button, ButtonGroup, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as Logo } from '../../../assets/svgs/logo.svg';
import PreviewButtons from '../buttons/PreviewButtons';

function PalmaPreview({ leatherColors, threadColors, resetColors, getEffectiveColor, stitchColor, engraving, generateShareUrl }) {
    const [currentSide, setCurrentSide] = useState('thumbSlotSide'); // 'thumbSlotSide' or 'cashFlapSide'
    const [filename, setFilename] = useState('');
    const shareDescription = 'Check out my custom Palma Wallet design!';

    useEffect(() => {
        const baseColorThumb = leatherColors?.find(c => c.id === getEffectiveColor('thumbSlotSideBase'))?.name;
        const baseColorCash = leatherColors?.find(c => c.id === getEffectiveColor('cashFlapSideBase'))?.name;
        const thumbSlotColor = leatherColors?.find(c => c.id === getEffectiveColor('thumbSlot'))?.name;
        const cashFlapColor = leatherColors?.find(c => c.id === getEffectiveColor('cashFlap'))?.name;
        const threadColor = threadColors?.find(c => c.hex === stitchColor)?.name;

        const newFilename = `${baseColorThumb}-${baseColorCash}-${thumbSlotColor}-${cashFlapColor}-${threadColor}-${currentSide}`;
        setFilename(newFilename);
    }, [leatherColors, threadColors, getEffectiveColor, stitchColor, currentSide]);

    const svgRef = useRef(null);

    const toggleView = (side) => {
        setCurrentSide(side);
    };

    return (
        <Stack
            sx={{
                width: '100%',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                direction: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3
            }}
        >
            <ButtonGroup variant="contained" sx={{ mt: 1 }}>
                <Button
                    onClick={() => toggleView('thumbSlotSide')}
                    data-testid="thumbSlotSide-button"
                    color={currentSide === 'thumbSlotSide' ? 'primary' : 'default'}
                >
                    Thumb Slot Side
                </Button>
                <Button
                    onClick={() => toggleView('cashFlapSide')}
                    data-testid="cashFlapSide-button"
                    color={currentSide === 'cashFlapSide' ? 'primary' : 'default'}
                >
                    Cash Flap Side
                </Button>
            </ButtonGroup>

            <Box width="100%" height="auto" sx={{ mt: 2 }}>
                <svg viewBox="0 0 108.5 71.588799" ref={svgRef}>

                    <defs>
                        <clipPath id="wallet-inside">
                            <path d="M.125.125v61.338c0 5.523 4.477 10 10 10H98.067c5.523 0 10-4.477 10-10V.125z" />
                        </clipPath>
                        <clipPath id="wallet-thumbSlot">
                            <path d="M.124 23.524v37.94c0 5.522 4.478 10 10.001 10H98.068c5.522 0 10-4.478 10-10v-45.69h-7c-16.721 0-33.442 4.135-46.877 4.206h-.19c-13.435-.071-30.156-4.205-46.877-4.205h-7zm42.63 10.426a5.272 5.272 0 0 1 5.273-5.07l12.148.01a5.29 5.29 0 0 1 5.235 6.03l-3.118 22.077a7 7 0 0 1-6.936 6.02l-2.445-.002a7.024 7.024 0 0 1-6.948-6.03L42.803 34.9a5.272 5.272 0 0 1-.049-.95z" />
                        </clipPath>
                        <clipPath id="wallet-cashFlap">
                            <path d="m94.224 71.464-.856-18.987c-.367-8.145-4.856-15.63-12.072-20.131L35.61 14.434c-4.42-2.756-9.604-4.226-14.904-4.226H.125l8.117.013H.125V61.463c0 5.523 4.477 10 10 10H90.742Z" strokeWidth=".35278" />
                        </clipPath>
                    </defs>

                    {/* Thumb Slot */}
                    {currentSide === 'thumbSlotSide' && (
                        <>
                            <g clipPath="url(#wallet-inside)">
                                <image
                                    data-testid={`thumbSlotBase-${getEffectiveColor('thumbSlotBase')}`}
                                    href={leatherColors?.find(c => c.id === getEffectiveColor(`thumbSlotBase`))?.texture}
                                    x="0"
                                    y="0"
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </g>
                            <g clipPath="url(#wallet-thumbSlot)">
                                <image
                                    data-testid={`thumbSlot-${getEffectiveColor('thumbSlot')}`}
                                    href={leatherColors?.find(c => c.id === getEffectiveColor('thumbSlot'))?.texture}
                                    x="0"
                                    y="0"
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </g>
                        </>
                    )}

                    {/* Cash Flap */}
                    {currentSide === 'cashFlapSide' && (
                        <>
                            <g clipPath="url(#wallet-inside)">
                                <image
                                    data-testid={`cashFlapBase-${getEffectiveColor('cashFlapBase')}`}
                                    href={leatherColors?.find(c => c.id === getEffectiveColor(`cashFlapBase`))?.texture}
                                    x="0"
                                    y="0"
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </g>
                            <g clipPath="url(#wallet-cashFlap)">
                                <image
                                    data-testid={`cashFlap-${getEffectiveColor('cashFlap')}`}
                                    href={leatherColors?.find(c => c.id === getEffectiveColor('cashFlap'))?.texture}
                                    x="0"
                                    y="0"
                                    width="100%"
                                    height="100%"
                                    preserveAspectRatio="xMidYMid slice"
                                />
                            </g>
                        </>
                    )}

                    {/* Engraving */}
                    {engraving && (
                        <g stroke="#000" strokeWidth={0.25} fill="#000" opacity={0.8}>
                            <text fontFamily="EB Garamond" fontSize={15} x={140} y={50} textAnchor="end">
                                {engraving}
                            </text>
                        </g>
                    )}

                    <g fill="none" stroke="#000" strokeWidth={0.25}>
                        <path d="M.125.125v61.338c0 5.523 4.477 10 10 10H98.067c5.523 0 10-4.477 10-10V.125z" />
                        {currentSide === 'thumbSlotSide' && (
                            <>
                                <path d="M.124 23.524v37.94c0 5.522 4.478 10 10.001 10H98.068c5.522 0 10-4.478 10-10v-45.69h-7c-16.721 0-33.442 4.135-46.877 4.206h-.19c-13.435-.071-30.156-4.205-46.877-4.205h-7zm42.63 10.426a5.272 5.272 0 0 1 5.273-5.07l12.148.01a5.29 5.29 0 0 1 5.235 6.03l-3.118 22.077a7 7 0 0 1-6.936 6.02l-2.445-.002a7.024 7.024 0 0 1-6.948-6.03L42.803 34.9a5.272 5.272 0 0 1-.049-.95z" />
                                {engraving && (
                                    <g fill="#000" strokeWidth={0} opacity={0.75}>
                                        <Logo height="20px" width="18px" x="15" y="35" />
                                    </g>
                                )}
                            </>
                        )}
                        {currentSide === 'cashFlapSide' && (
                            <>
                                <path d="m94.224 71.464-.856-18.987c-.367-8.145-4.856-15.63-12.072-20.131L35.61 14.434c-4.42-2.756-9.604-4.226-14.904-4.226H.125l8.117.013H.125V61.463c0 5.523 4.477 10 10 10H90.742Z" strokeWidth=".35278" />
                                {engraving && (
                                    <g stroke="#000" strokeWidth={0.25} fill='#000' opacity={0.8}>
                                        <text fontFamily='EB Garamond' fontSize={15} x={15} y={55} textAnchor='start'>{engraving}</text>
                                    </g>
                                )}
                                {!engraving && (
                                    <g fill="#000" strokeWidth={0} opacity={0.75}>
                                        <Logo height="20px" width="18px" x="15" y="35" />
                                    </g>
                                )}
                            </>
                        )}
                    </g>


                    {/* Stitch Lines */}
                    <g id="stitchLine" data-testid="stitch-line" fill="none" strokeOpacity={1} strokeDashoffset={2} stroke={stitchColor} strokeWidth={0.75} strokeDasharray={[2, 2]} transform="translate(-734.88805,-202.9791)">
                        <path
                            d="
                                M 229.04687,128.88086 
                                H 298.375 
                                M 298.375,228.80469 
                                h -69.32813 
                                c -3.35563,0 -5.99023,-2.63429 -5.99023,-5.99024 
                                v -42.94336 -2.05664 -42.94336 
                                c 0,-3.35546 2.63477,-5.99023 5.99023,-5.99023
                            "
                            transform="rotate(-90,551.81537,-58.326235)" />
                    </g>
                </svg>
            </Box>

            <PreviewButtons
                resetColors={resetColors}
                shareDescription={shareDescription}
                generateShareUrl={generateShareUrl}
                svgRef={svgRef}
                filename={filename}
            />
        </Stack>
    );
}

PalmaPreview.propTypes = {
    leatherColors: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        swatch: PropTypes.string.isRequired,
        texture: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
    })).isRequired,
    threadColors: PropTypes.arrayOf(PropTypes.shape({
        hex: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
    })).isRequired,
    getEffectiveColor: PropTypes.func.isRequired,
    resetColors: PropTypes.func.isRequired,
    stitchColor: PropTypes.string.isRequired,
    engraving: PropTypes.string,
    generateShareUrl: PropTypes.func.isRequired
};

export default PalmaPreview;
